import { KuxProgramType } from '@kaltura-ott/tvpil';
import { action, computed } from 'mobx';
import { container, singleton } from 'tsyringe';

import type { BingeWatchingSettings, PlayoutStoreV2, PlayoutStoreV2InitArgs, Program } from '../stores';
import type { TimeWatcher } from '../stores/category/playout/time-watcher';
import type { PlayableAsset } from '../stores/entities/PlayableAsset';

@singleton()
export class PlayoutStoreFacade {
  private timeWatcher?: TimeWatcher;
  public get originalStore() {
    return container.resolve<PlayoutStoreV2>('playoutStore');
  }

  public set isMaxiPlayer(value: boolean) {
    this.originalStore.isMaxiPlayer = value;
  }

  public get isMaxiPlayer() {
    return this.originalStore.isMaxiPlayer;
  }

  public isMulticastPlayback(): Promise<boolean> {
    return this.originalStore.isMulticastPlayback();
  }

  public hasMulticastFiles(asset: PlayableAsset) {
    return this.originalStore.hasMulticastFiles(asset);
  }

  public isIpTvMode() {
    return this.originalStore.isIpTvMode();
  }

  public get currentAsset() {
    return this.originalStore.subAsset ? this.originalStore.subAsset : this.originalStore.currentAsset;
  }

  public get mosaicAsset() {
    return this.originalStore.currentAsset;
  }

  public get type() {
    return this.originalStore.type;
  }

  public get loadingNextProgram() {
    return this.timeWatcher?.loadingNextProgram;
  }

  public get showNextButtonTime() {
    return this.timeWatcher?.showNextButtonTime;
  }

  public get showNextButtonDuration() {
    return this.timeWatcher?.showNextButtonDuration;
  }

  public get smartNextEpisode() {
    return this.timeWatcher?.smartNextEpisode;
  }

  public get autoNextEpisode() {
    return this.timeWatcher?.autoNextEpisode;
  }

  public get showSeriesPage() {
    return this.timeWatcher?.showSeriesPage;
  }

  public get autoplayNextProgram() {
    return this.originalStore.autoplayNextProgram;
  }

  public get isStartOver() {
    return this.originalStore.isStartOver;
  }

  @action.bound
  async init(params: PlayoutStoreV2InitArgs): Promise<void> {
    this.timeWatcher = container.resolve<TimeWatcher>('timeWatcher');

    await this.originalStore.init(params);
    await this.timeWatcher.init();
  }

  @action.bound
  async setCurrentTime(
    currentTime: number,
    duration: number,
    bingeWatchingSettings?: BingeWatchingSettings,
  ): Promise<void> {
    return this.timeWatcher?.setCurrentTime(currentTime, duration, bingeWatchingSettings);
  }

  @action.bound
  stopPlayback(): void {
    return this.originalStore.stopPlayback();
  }

  @action.bound
  openByDigitWizard(): void {
    return this.originalStore.openByDigitWizard();
  }

  @action.bound
  reset() {
    return this.originalStore.reset();
  }

  async onStreamEnd() {
    await this.timeWatcher?.onStreamEnd();
  }

  async getNextAssetToPlay() {
    await this.timeWatcher?.getNextAssetToPlay();
  }

  @computed get FFEnabled(): boolean {
    return this.originalStore.FFEnabled;
  }

  getCurrentPermanentLinearAssetId(): number | undefined {
    return this.originalStore.getCurrentPermanentLinearAssetId();
  }

  getCurrentPermanentProgram(): KuxProgramType | undefined {
    return this.originalStore.getCurrentPermanentProgram();
  }

  isMosaicPlayback(): boolean {
    return this.originalStore.isMosaicPlayback();
  }

  getCurrentProgram(): Program | undefined {
    return this.originalStore.getCurrentProgram();
  }

  getCurrentProgramId(): number | undefined {
    return this.originalStore.getCurrentProgramId();
  }
}
